import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Button, Checkbox, SegmentedControl, SegmentedControlSize, ToggleHeight } from '@column/column-ui-kit';

import { PopoverAction, PopoverBase, PopoverBaseProps } from '../Base';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { formatTransferType } from '~/lib/transfers/utils';
import { TransferEntryType, TransferTypeOptions, isBatchTypeValue } from '~/repositories/Transfer/TransferRepository';
import { Line } from '~/styles';
import { formatString } from '~/util';

export interface PopoverFilterTransferTypeData {
  type?: (typeof TransferTypeOptions)[];
  isIncoming?: boolean;
}

interface PopoverFilterTransferTypeProps extends PopoverBaseProps {
  data: PopoverFilterTransferTypeData;
  onSubmit?: (data: PopoverFilterTransferTypeData) => void;
  showBatchTypes?: boolean;
  typeOptions?: readonly string[];
  hideDirection?: boolean;
}

const Wrapper = styled(PopoverBase)`
  --popover-padding: 0px;
  --popover-width: 248px;
  --popover-left: 0px;
`;

const List = styled.div`
  padding: 8px;
  margin-bottom: -12px;
`;

const BatchEntries = styled(ToggleHeight)`
  --toggle-height-padding: 0;
`;

export const ListLabel = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.secondary.blendToBackground(700)};
  text-transform: uppercase;
  padding: 8px;
`;

const Entry = styled.div<{ $isActive: boolean }>`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: ${({ theme }) => theme.foreground};
  transition: background-color 0.15s;
  border-radius: 6px;
  text-transform: capitalize;

  &:hover {
    background-color: ${({ theme }) => theme.secondary.blendToBackground(75)};
  }
`;

const Divider = styled(Line)`
  margin: 8px 0;
`;

const TYPE_ENTRIES: string[] = TransferTypeOptions.flatMap((entry) => {
  if (entry === 'ach_credit') return ['ach_batch', entry];
  if (entry === 'check_credit') return ['check_batch', entry];
  return [entry];
});

export const PopoverFilterTransferType: React.FC<PopoverFilterTransferTypeProps> = ({
  data,
  onSubmit,
  showBatchTypes,
  typeOptions = TYPE_ENTRIES,
  hideDirection,
  ...popoverProps
}) => {
  const [currentData, setCurrentData] = useState<PopoverFilterTransferTypeData>({});

  const cryptoEnabled = useFeatureFlag(FeatureFlag.EnableLiquidationRouteAPIs);

  const isActive = useCallback(
    (key: string) => {
      const optionKey = key as unknown as typeof TransferTypeOptions;

      return !!(currentData.type && currentData.type.includes(optionKey));
    },
    [currentData]
  );

  const handleOptionChange = useCallback(
    (key: string) => {
      const optionKey = key as unknown as typeof TransferTypeOptions;

      if (isActive(key)) {
        setCurrentData((s) => ({ ...s, type: currentData.type?.filter((k) => k !== optionKey) }));
        return;
      }
      setCurrentData((s) => ({ ...s, type: [...(s.type ?? []), optionKey] }));
    },
    [currentData]
  );

  const handleResetClick = () => {
    setCurrentData({});

    if (onSubmit) {
      onSubmit({});
    }
  };

  const isBatchListVisible = typeof currentData.isIncoming !== 'boolean';

  const handleApplyClick = () => {
    if (!onSubmit) return;
    if (isBatchListVisible) {
      onSubmit(currentData);
      return;
    }
    onSubmit({
      ...currentData,
      type: currentData.type?.filter((entry) => !isBatchTypeValue(entry)),
    });
  };

  useEffect(() => {
    setCurrentData(data);
  }, [data, popoverProps.show]);

  return (
    <Wrapper {...popoverProps}>
      <List>
        <ListLabel>Filter type</ListLabel>
        {!hideDirection && (
          <>
            <SegmentedControl
              size={SegmentedControlSize.Small}
              options={[
                {
                  label: 'All',
                  value: undefined,
                },
                {
                  label: 'Incoming',
                  value: true,
                },
                {
                  label: 'Outgoing',
                  value: false,
                },
              ]}
              fullWidth
              activeUndefined
              active={currentData.isIncoming}
              onOptionChange={(value) =>
                setCurrentData((s) => ({
                  ...s,
                  isIncoming: typeof value === 'boolean' ? value : undefined,
                  type: typeof value !== 'boolean' ? s.type : s.type?.filter((entry) => !isBatchTypeValue(entry)),
                }))
              }
            />
            <Divider />
          </>
        )}
        {typeOptions
          // Hide the stablecoin (crypto) type unless the platform has the crypto flag enabled.
          .filter((entry) => entry !== 'crypto' || cryptoEnabled)
          .map((entry) => {
            const isBatch = isBatchTypeValue(entry);
            if (isBatch && !showBatchTypes) return null;
            const row = (
              <Entry key={entry} $isActive={isActive(entry)} onClick={() => handleOptionChange(entry)}>
                {isBatch
                  ? formatTransferType(entry as TransferEntryType)
                  : formatString(entry)
                      .replaceAll('Ach', 'ACH')
                      .replaceAll('Swift', 'International Wire')
                      .replaceAll('Check credit', 'Deposited Check')
                      .replaceAll('Check debit', 'Issued Check')
                      .replaceAll('Crypto', 'Stablecoin')}
                <Checkbox isChecked={isActive(entry)} onCheckedChange={() => handleOptionChange(entry)} />
              </Entry>
            );
            return isBatch ? (
              <BatchEntries key={entry} isClose={!isBatchListVisible}>
                {row}
              </BatchEntries>
            ) : (
              row
            );
          })}
      </List>
      <PopoverAction>
        <Button size="small" variant="secondary" onClick={handleResetClick}>
          Reset
        </Button>
        <Button size="small" onClick={handleApplyClick}>
          Apply
        </Button>
      </PopoverAction>
    </Wrapper>
  );
};
